/**
 * iOS-style haptic feedback utility.
 * Uses navigator.vibrate() as a PWA-compatible fallback.
 * On iOS Safari PWA, vibration API is limited — these are best-effort.
 */

type HapticStyle = 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error' | 'selection';

const patterns: Record<HapticStyle, number | number[]> = {
  light: 10,
  medium: 20,
  heavy: 30,
  success: [10, 50, 10],
  warning: [15, 40, 15],
  error: [20, 30, 20, 30, 20],
  selection: 5,
};

function vibrate(pattern: number | number[]): void {
  try {
    if ('vibrate' in navigator) {
      navigator.vibrate(pattern);
    }
  } catch {
    // Silently fail — haptics are non-essential
  }
}

/** Light tap — tab switches, toggles, minor selections */
export function hapticLight(): void {
  vibrate(patterns.light);
}

/** Medium impact — confirming an action, opening a sheet */
export function hapticMedium(): void {
  vibrate(patterns.medium);
}

/** Heavy impact — destructive action confirmation */
export function hapticHeavy(): void {
  vibrate(patterns.heavy);
}

/** Success pattern — save completed, upload done */
export function hapticSuccess(): void {
  vibrate(patterns.success);
}

/** Warning pattern — unsaved changes, validation error */
export function hapticWarning(): void {
  vibrate(patterns.warning);
}

/** Error pattern — failed action */
export function hapticError(): void {
  vibrate(patterns.error);
}

/** Tiny selection tick — entering selection mode, photo select */
export function hapticSelection(): void {
  vibrate(patterns.selection);
}
